import * as React from "react"
import { connect } from "react-redux"
import { ActionAnchor } from "@tenforce/toolbox-button-push-react"

import bem from "../../../bem"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { GlobalState } from "../../../store"
import { DispatchProps, mapDispatchToProps } from "../actions"

import "./Tutorial.scss"
import { Labels } from "../../../util/Translations"

const b = bem.with("NewUI__tutorial")

type StateProps = Tutorial.Tutorial

type Props = StateProps & DispatchProps

interface State {
    stepRefs: React.RefObject<HTMLDivElement>[]
}

export class Tutorial extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { stepRefs: [] }
    }

    static getDerivedStateFromProps(props: Props): State {
        return {
            stepRefs: (props.content?.tutorialLines ?? []).map(() =>
                React.createRef<HTMLDivElement>(),
            ),
        }
    }

    render(): JSX.Element {
        const { content, loaded } = this.props
        const { TutorialNotAvailable } = Labels

        if (!content && loaded) {
            return <div className={b()}>{TutorialNotAvailable}</div>
        }

        if (!content) {
            return <div className={b()} />
        }

        const ratio = this.calculateRatio(
            content.tutorialLines.filter((line) => !line.isExtraStep).length,
        )

        return (
            <div className={b()}>
                <div className="tw-flex tw-flex-row tw-items-start tw-p-4">
                    <div className="tw-w-3/4 tw-m-4">
                        <div className={b("main")}>
                            <section
                                className={`${b("head")} tw-py-8 tw-px-12`}
                            >
                                <div className="tw-mb-8 tw-text-right">
                                    <h6 className="is-dimmed">
                                        {Symbols.TenForceTextLogo}
                                    </h6>
                                </div>
                                <h2
                                    dangerouslySetInnerHTML={{
                                        __html: this.stripStyle(content.title),
                                    }}
                                />
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: this.stripStyle(
                                            content.description,
                                        ),
                                    }}
                                />
                                <div className="tw-mt-4">
                                    <nav className="tw-flex tw-flex-row">
                                        {content.tutorialLines.map(
                                            (line, i) =>
                                                !line.isExtraStep && (
                                                    <ActionAnchor
                                                        key={i}
                                                        title={line.title}
                                                        extraClasses={`tw-flex-col-center tw-flex-1 tw-m-2 tw-w-${ratio}`}
                                                        onClick={(): void =>
                                                            this.scrollToRef(
                                                                this.state
                                                                    .stepRefs[
                                                                    i
                                                                ],
                                                            )
                                                        }
                                                    >
                                                        <h3>
                                                            {!!line.counter ? (
                                                                line.counter
                                                            ) : (
                                                                <>&nbsp;</>
                                                            )}
                                                        </h3>
                                                        <div
                                                            className={`${b(
                                                                "label",
                                                            )} label tw-w-full tw-text-center`}
                                                        >
                                                            {line.title}
                                                        </div>
                                                    </ActionAnchor>
                                                ),
                                        )}
                                    </nav>
                                    <div
                                        className={`${b(
                                            "progress-bar",
                                        )} theme-progressbar`}
                                    />
                                </div>
                            </section>
                            {content.tutorialLines.map((line, i) => (
                                <React.Fragment key={i}>
                                    <div
                                        className={`${b(
                                            "step-separator",
                                        )} separator-line is-dashed`}
                                    />
                                    <section
                                        className={`${b(
                                            "step",
                                        )} tw-py-8 tw-px-12 tw-overflow-auto`}
                                    >
                                        <h3
                                            className={`${b(
                                                "step-heading",
                                            )} tw-mb-4`}
                                            ref={this.state.stepRefs[i]}
                                        >
                                            {line.counter} {line.title}
                                        </h3>
                                        <div
                                            className={b("step-description")}
                                            dangerouslySetInnerHTML={{
                                                __html: this.stripStyle(
                                                    line.content,
                                                ),
                                            }}
                                        />
                                    </section>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                    <div
                        className={`${b(
                            "aside",
                        )} tw-w-1/4 tw-m-4 tw-overflow-auto`}
                    >
                        {!!content.sideInformation && (
                            <div
                                className={`${b(
                                    "side-information",
                                )} tw-overflow-auto`}
                                dangerouslySetInnerHTML={{
                                    __html: this.stripStyle(
                                        content.sideInformation,
                                    ),
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        )
    }

    stripStyle(html: string): string {
        return html.replace(/style=("(.*?)"|'(.*?)')/gi, "")
    }

    scrollToRef(ref: React.RefObject<HTMLDivElement>): void {
        if (!!ref.current) {
            window.scroll({
                top:
                    ref.current.offsetTop +
                    (document.getElementById("newui-resource-header")
                        ?.offsetHeight ?? 0),
                behavior: "smooth",
            })
        }
    }

    calculateRatio(length: number): string {
        if (length <= 0) {
            return "full"
        } else if (length <= 6) {
            return `1/${length}`
        } else if (length <= 8) {
            return "1/8"
        } else {
            return "1/12"
        }
    }
}

const mapStateToProps = (state: GlobalState): StateProps => state.tutorial

export default connect(mapStateToProps, mapDispatchToProps)(Tutorial)
